import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fmtDate } from "@/lib/locale";
import { AccessRestricted } from "@/components/access-restricted";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { FinancialStatement } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ChevronLeft, ChevronRight, CheckCircle2, FileSpreadsheet, Plus, Info,
  TrendingUp, TrendingDown, Minus, Download, Eye, Sparkles, Trash2, Pencil,
} from "lucide-react";
import {
  type CustomLineItem,
  type FloorplanLender,
  type PLData,
  type PLRatios,
  type BSData,
  type BSTotals,
  n,
  fmt$,
  fmtPct,
  computePLRatios,
  computeBSTotals,
  exportPLPDF,
  exportBSPDF,
  buildPLDataFromStatement,
  buildBSDataFromStatement,
} from "@/lib/financialStatementPdf";
import { FormattedNumberInput } from "@/components/formatted-number-input";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeId() {
  return Math.random().toString(36).slice(2, 9);
}

// Thin wrapper over the shared FormattedNumberInput: callers here work with
// number | "" values.
function NumericInput({
  value, onChange, prefix, suffix, placeholder, testId,
}: {
  value: number | "";
  onChange: (v: number | "") => void;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  testId?: string;
}) {
  return (
    <FormattedNumberInput
      value={value === "" ? null : value}
      onValueChange={(_raw, num) => onChange(num ?? "")}
      prefix={prefix}
      suffix={suffix}
      placeholder={placeholder ?? "0"}
      testId={testId}
      className="text-sm"
    />
  );
}

function TipBox({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 mb-5 p-4 rounded-xl bg-primary/5 border border-primary/15">
      <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
      <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function FieldRow({ label, hint, children, optional }: { label: string; hint?: string; children: React.ReactNode; optional?: boolean }) {
  const { t } = useTranslation(["financial", "common"]);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-sm">{label}</Label>
        {optional && <span className="text-xs text-muted-foreground">{t("financial:common.optional")}</span>}
        {hint && (
          <span title={hint} className="cursor-help">
            <Info className="w-3.5 h-3.5 text-muted-foreground" />
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

type BSPeriodPrecision = "exact" | "year_only";
type BSFormData = BSData & {
  asOfDate: string;
  periodYear: string;
  periodPrecision: BSPeriodPrecision;
};

function bsPeriodLabel(stmt: FinancialStatement) {
  if (stmt.asOfDate) return `As of ${fmtDate(stmt.asOfDate)}`;
  if (stmt.periodYear != null) return `Year-only: ${stmt.periodYear}`;
  const legacyRange = stmt.periodStart && stmt.periodEnd
    ? `${stmt.periodStart} – ${stmt.periodEnd}`
    : stmt.periodStart || stmt.periodEnd;
  return legacyRange
    ? `Unverified legacy period: ${legacyRange}`
    : "Unverified legacy period";
}

function statementSourceLabel(sourceType: string | null | undefined) {
  switch (sourceType) {
    case "manual": return "Manual";
    case "uploaded_document": return "Uploaded document";
    default: return "Legacy / unknown";
  }
}

// ─── Custom Line Items UI ──────────────────────────────────────────────────────

function CustomLineItemsSection({
  section,
  items,
  onChange,
}: {
  section: string;
  items: CustomLineItem[];
  onChange: (items: CustomLineItem[]) => void;
}) {
  const { t } = useTranslation(["financial", "common"]);
  const sectionItems = items.filter(i => i.section === section);

  const addItem = () => {
    onChange([...items, { id: makeId(), section, label: "", amount: "" }]);
  };

  const updateItem = (id: string, patch: Partial<CustomLineItem>) => {
    onChange(items.map(i => i.id === id ? { ...i, ...patch } : i));
  };

  const removeItem = (id: string) => {
    onChange(items.filter(i => i.id !== id));
  };

  return (
    <div className="col-span-full space-y-2 mt-2">
      {sectionItems.map(item => (
        <div key={item.id} className="flex gap-2 items-start">
          <div className="flex-1">
            <Input
              value={item.label}
              onChange={e => updateItem(item.id, { label: e.target.value })}
              placeholder={t("financial:customItems.labelPlaceholder")}
              className="text-sm"
              data-testid={`input-custom-label-${item.id}`}
            />
          </div>
          <div className="w-36">
            <NumericInput
              value={item.amount}
              onChange={v => updateItem(item.id, { amount: v })}
              prefix="$"
              testId={`input-custom-amount-${item.id}`}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => removeItem(item.id)}
            className="h-9 w-9 text-muted-foreground hover:text-rose-500"
            data-testid={`button-remove-custom-${item.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={addItem}
        className="gap-1.5 text-xs h-8"
        data-testid={`button-add-custom-${section}`}
      >
        <Plus className="w-3 h-3" /> {t("financial:customItems.add")}
      </Button>
    </div>
  );
}


function RatioIndicator({ label, value, format, thresholds, hint }: {
  label: string;
  value: number | null;
  format: (v: number) => string;
  thresholds?: { warn: number; danger: number; higherIsBetter?: boolean };
  hint?: string;
}) {
  const { t } = useTranslation(["financial", "common"]);
  let status: "good" | "warn" | "danger" | "neutral" = "neutral";
  if (value !== null && thresholds) {
    if (thresholds.higherIsBetter) {
      if (value < thresholds.danger) status = "danger";
      else if (value < thresholds.warn) status = "warn";
      else status = "good";
    } else {
      if (value >= thresholds.danger) status = "danger";
      else if (value >= thresholds.warn) status = "warn";
      else status = "good";
    }
  }

  const colors = {
    good: "text-emerald-600 dark:text-emerald-400",
    warn: "text-amber-600 dark:text-amber-400",
    danger: "text-rose-600 dark:text-rose-400",
    neutral: "text-muted-foreground",
  };

  const icons = {
    good: <TrendingUp className="w-3.5 h-3.5" />,
    warn: <Minus className="w-3.5 h-3.5" />,
    danger: <TrendingDown className="w-3.5 h-3.5" />,
    neutral: null,
  };

  const labels = { good: t("financial:ratioStatus.good"), warn: t("financial:ratioStatus.warn"), danger: t("financial:ratioStatus.danger"), neutral: "" };

  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0 border-border/50">
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-foreground">{label}</span>
          {hint && <span title={hint} className="cursor-help"><Info className="w-3 h-3 text-muted-foreground" /></span>}
        </div>
        {thresholds && status !== "neutral" && (
          <span className={`text-xs ${colors[status]} flex items-center gap-0.5`}>
            {icons[status]} {labels[status]}
          </span>
        )}
      </div>
      <span className={`text-sm font-bold tabular-nums ${value !== null ? colors[status] : "text-muted-foreground"}`}>
        {value !== null ? format(value) : "—"}
      </span>
    </div>
  );
}

// ─── P&L Wizard Steps ─────────────────────────────────────────────────────────

function PLStep1({ data, onChange }: { data: PLData; onChange: (d: Partial<PLData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:pl.step1Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.periodStart")}>
          <Input type="date" value={data.periodStart} onChange={e => onChange({ periodStart: e.target.value })} data-testid="input-pl-period-start" />
        </FieldRow>
        <FieldRow label={t("financial:fields.periodEnd")}>
          <Input type="date" value={data.periodEnd} onChange={e => onChange({ periodEnd: e.target.value })} data-testid="input-pl-period-end" />
        </FieldRow>
        <FieldRow label={t("financial:fields.dealershipName")} hint={t("financial:hints.dealershipName")}>
          <Input value={data.dealershipName} onChange={e => onChange({ dealershipName: e.target.value })} placeholder={t("financial:placeholders.dealershipName")} data-testid="input-pl-dealership-name" />
        </FieldRow>
        <FieldRow label={t("financial:fields.address")} optional>
          <Input value={data.address} onChange={e => onChange({ address: e.target.value })} placeholder={t("financial:placeholders.address")} data-testid="input-pl-address" />
        </FieldRow>
        <FieldRow label={t("financial:fields.businessState")} hint={t("financial:hints.businessState")} optional>
          <Input value={data.businessState} onChange={e => onChange({ businessState: e.target.value })} placeholder={t("financial:placeholders.businessState")} data-testid="input-pl-business-state" />
        </FieldRow>
      </div>
    </div>
  );
}

function PLStep2({ data, onChange }: { data: PLData; onChange: (d: Partial<PLData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const recomputeTotal = (patch: Partial<PLData>): Partial<PLData> => {
    const merged = { ...data, ...patch };
    const newTotal = n(merged.newVehicleRevenue) + n(merged.usedRetailRevenue) + n(merged.wholesaleRevenue) + n(merged.fiRevenue) + n(merged.docFeesCollected) + n(merged.servicePartsRevenue)
      + merged.customLineItems.filter(i => i.section === "pl-revenue").reduce((s, i) => s + n(i.amount), 0);
    return { ...patch, totalRevenue: (newTotal > 0 ? newTotal : "") as number | "" };
  };

  const derivedTotal = n(data.newVehicleRevenue) + n(data.usedRetailRevenue) + n(data.wholesaleRevenue) + n(data.fiRevenue) + n(data.docFeesCollected) + n(data.servicePartsRevenue)
    + data.customLineItems.filter(i => i.section === "pl-revenue").reduce((s, i) => s + n(i.amount), 0);

  return (
    <div className="space-y-5">
      <TipBox text={t("financial:pl.step2Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.newVehicleRevenue")} hint={t("financial:hints.newVehicleRevenue")} optional>
          <NumericInput value={data.newVehicleRevenue} onChange={v => onChange(recomputeTotal({ newVehicleRevenue: v }))} prefix="$" testId="input-pl-new-vehicle-revenue" />
        </FieldRow>
        <FieldRow label={t("financial:fields.usedRetailRevenue")} hint={t("financial:hints.usedRetailRevenue")}>
          <NumericInput value={data.usedRetailRevenue} onChange={v => onChange(recomputeTotal({ usedRetailRevenue: v }))} prefix="$" testId="input-pl-used-retail-revenue" />
        </FieldRow>
        <FieldRow label={t("financial:fields.wholesaleRevenue")} hint={t("financial:hints.wholesaleRevenue")} optional>
          <NumericInput value={data.wholesaleRevenue} onChange={v => onChange(recomputeTotal({ wholesaleRevenue: v }))} prefix="$" testId="input-pl-wholesale-revenue" />
        </FieldRow>
        <FieldRow label={t("financial:fields.fiRevenue")} hint={t("financial:hints.fiRevenue")}>
          <NumericInput value={data.fiRevenue} onChange={v => onChange(recomputeTotal({ fiRevenue: v }))} prefix="$" testId="input-pl-fi-revenue" />
        </FieldRow>
        <FieldRow label={t("financial:fields.docFeesCollected")} hint={t("financial:hints.docFeesCollected")} optional>
          <NumericInput value={data.docFeesCollected} onChange={v => onChange(recomputeTotal({ docFeesCollected: v }))} prefix="$" testId="input-pl-doc-fees" />
        </FieldRow>
        <FieldRow label={t("financial:fields.servicePartsRevenue")} hint={t("financial:hints.servicePartsRevenue")} optional>
          <NumericInput value={data.servicePartsRevenue} onChange={v => onChange(recomputeTotal({ servicePartsRevenue: v }))} prefix="$" testId="input-pl-service-parts-revenue" />
        </FieldRow>
        <FieldRow label={t("financial:fields.unitsSold")} hint={t("financial:hints.unitsSold")}>
          <NumericInput value={data.unitsSold} onChange={v => onChange({ unitsSold: v })} suffix={t("financial:units.units")} testId="input-pl-units-sold" />
        </FieldRow>
        <FieldRow label={t("financial:fields.avgFrontEndGross")} hint={t("financial:hints.avgFrontEndGross")} optional>
          <NumericInput value={data.avgFrontEndGross} onChange={v => onChange({ avgFrontEndGross: v })} prefix="$" testId="input-pl-front-end-gross" />
        </FieldRow>
        <FieldRow label={t("financial:fields.avgBackEndGross")} hint={t("financial:hints.avgBackEndGross")} optional>
          <NumericInput value={data.avgBackEndGross} onChange={v => onChange({ avgBackEndGross: v })} prefix="$" testId="input-pl-back-end-gross" />
        </FieldRow>
        <CustomLineItemsSection
          section="pl-revenue"
          items={data.customLineItems}
          onChange={items => onChange(recomputeTotal({ customLineItems: items }))}
        />
      </div>
      {derivedTotal > 0 && (
        <div className="p-3 rounded-xl bg-primary/5 border border-primary/15 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("financial:pl.totalRevenueSum")}</span>
            <span className="font-semibold">{fmt$(derivedTotal)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PLStep3({ data, onChange }: { data: PLData; onChange: (d: Partial<PLData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const rev = n(data.totalRevenue);

  const recomputeCogs = (patch: Partial<PLData>): Partial<PLData> => {
    const merged = { ...data, ...patch };
    const total = n(merged.newVehicleCogs) + n(merged.usedRetailCogs) + n(merged.wholesaleCogs) + n(merged.packPerUnit) * n(merged.unitsSold) + n(merged.auctionFees) + n(merged.otherCogs)
      + merged.customLineItems.filter(i => i.section === "pl-cogs").reduce((s, i) => s + n(i.amount), 0);
    const gp: number | "" = rev > 0 && total > 0 ? rev - total : "";
    return { ...patch, costOfGoodsSold: (total > 0 ? total : "") as number | "", grossProfit: gp };
  };

  const packTotal = n(data.packPerUnit) * n(data.unitsSold);
  const derivedCogs = n(data.newVehicleCogs) + n(data.usedRetailCogs) + n(data.wholesaleCogs) + packTotal + n(data.auctionFees) + n(data.otherCogs)
    + data.customLineItems.filter(i => i.section === "pl-cogs").reduce((s, i) => s + n(i.amount), 0);
  const derivedGP = rev > 0 && derivedCogs > 0 ? rev - derivedCogs : 0;

  return (
    <div className="space-y-5">
      <TipBox text={t("financial:pl.step3Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {n(data.newVehicleRevenue) > 0 && (
          <FieldRow label={t("financial:fields.newVehicleCogs")} hint={t("financial:hints.newVehicleCogs")} optional>
            <NumericInput value={data.newVehicleCogs} onChange={v => onChange(recomputeCogs({ newVehicleCogs: v }))} prefix="$" testId="input-pl-new-vehicle-cogs" />
          </FieldRow>
        )}
        <FieldRow label={t("financial:fields.usedRetailCogs")} hint={t("financial:hints.usedRetailCogs")}>
          <NumericInput value={data.usedRetailCogs} onChange={v => onChange(recomputeCogs({ usedRetailCogs: v }))} prefix="$" testId="input-pl-used-retail-cogs" />
        </FieldRow>
        <FieldRow label={t("financial:fields.wholesaleCogs")} hint={t("financial:hints.wholesaleCogs")} optional>
          <NumericInput value={data.wholesaleCogs} onChange={v => onChange(recomputeCogs({ wholesaleCogs: v }))} prefix="$" testId="input-pl-wholesale-cogs" />
        </FieldRow>
        <FieldRow label={t("financial:fields.packPerUnit")} hint={t("financial:hints.packPerUnit")} optional>
          <NumericInput value={data.packPerUnit} onChange={v => onChange(recomputeCogs({ packPerUnit: v }))} prefix="$" suffix={t("financial:units.perUnit")} testId="input-pl-pack-per-unit" />
        </FieldRow>
        <FieldRow label={t("financial:fields.auctionFees")} hint={t("financial:hints.auctionFees")} optional>
          <NumericInput value={data.auctionFees} onChange={v => onChange(recomputeCogs({ auctionFees: v }))} prefix="$" testId="input-pl-auction-fees" />
        </FieldRow>
        <FieldRow label={t("financial:fields.otherCogs")} hint={t("financial:hints.otherCogs")} optional>
          <NumericInput value={data.otherCogs} onChange={v => onChange(recomputeCogs({ otherCogs: v }))} prefix="$" testId="input-pl-other-cogs" />
        </FieldRow>
        <CustomLineItemsSection
          section="pl-cogs"
          items={data.customLineItems}
          onChange={items => onChange(recomputeCogs({ customLineItems: items }))}
        />
      </div>
      {n(data.packPerUnit) > 0 && n(data.unitsSold) > 0 && (
        <div className="p-3 rounded-xl bg-muted/50 border text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("financial:pl.packTotal", { units: n(data.unitsSold), amount: fmt$(n(data.packPerUnit)) })}</span>
            <span className="font-medium">{fmt$(packTotal)}</span>
          </div>
        </div>
      )}
      {(rev > 0 || derivedCogs > 0) && (
        <div className="p-4 rounded-xl bg-primary/5 border border-primary/15 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t("financial:pl.totalRevenue")}</span>
            <span className="font-semibold">{rev > 0 ? fmt$(rev) : "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t("financial:pl.totalCogs")}</span>
            <span className="font-semibold">{derivedCogs > 0 ? fmt$(derivedCogs) : n(data.costOfGoodsSold) > 0 ? fmt$(n(data.costOfGoodsSold)) : "—"}</span>
          </div>
          <Separator />
          <div className="flex justify-between text-sm font-bold">
            <span>{t("financial:pl.grossProfit")}</span>
            <span className={derivedGP > 0 ? "text-emerald-600 dark:text-emerald-400" : ""}>{derivedGP > 0 ? fmt$(derivedGP) : n(data.grossProfit) > 0 ? fmt$(n(data.grossProfit)) : "—"}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PLStep4({ data, onChange }: { data: PLData; onChange: (d: Partial<PLData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const totalOpEx = n(data.payroll) + n(data.rentMortgage) + n(data.utilities) + n(data.advertising) + n(data.insurance) + n(data.dmsFees) + n(data.dealerPrincipalComp) + n(data.otherOpEx)
    + data.customLineItems.filter(i => i.section === "pl-opex").reduce((s, i) => s + n(i.amount), 0);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:pl.step4Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.payroll")} hint={t("financial:hints.payroll")}>
          <NumericInput value={data.payroll} onChange={v => onChange({ payroll: v })} prefix="$" testId="input-pl-payroll" />
        </FieldRow>
        <FieldRow label={t("financial:fields.rentMortgage")} hint={t("financial:hints.rentMortgage")}>
          <NumericInput value={data.rentMortgage} onChange={v => onChange({ rentMortgage: v })} prefix="$" testId="input-pl-rent" />
        </FieldRow>
        <FieldRow label={t("financial:fields.utilities")} hint={t("financial:hints.utilities")}>
          <NumericInput value={data.utilities} onChange={v => onChange({ utilities: v })} prefix="$" testId="input-pl-utilities" />
        </FieldRow>
        <FieldRow label={t("financial:fields.advertising")} hint={t("financial:hints.advertising")}>
          <NumericInput value={data.advertising} onChange={v => onChange({ advertising: v })} prefix="$" testId="input-pl-advertising" />
        </FieldRow>
        <FieldRow label={t("financial:fields.insurance")} hint={t("financial:hints.insurance")}>
          <NumericInput value={data.insurance} onChange={v => onChange({ insurance: v })} prefix="$" testId="input-pl-insurance" />
        </FieldRow>
        <FieldRow label={t("financial:fields.dmsFees")} hint={t("financial:hints.dmsFees")} optional>
          <NumericInput value={data.dmsFees} onChange={v => onChange({ dmsFees: v })} prefix="$" testId="input-pl-dms-fees" />
        </FieldRow>
        <FieldRow label={t("financial:fields.dealerPrincipalComp")} hint={t("financial:hints.dealerPrincipalComp")} optional>
          <NumericInput value={data.dealerPrincipalComp} onChange={v => onChange({ dealerPrincipalComp: v })} prefix="$" testId="input-pl-dealer-comp" />
        </FieldRow>
        <FieldRow label={t("financial:fields.otherOpEx")} hint={t("financial:hints.otherOpEx")} optional>
          <NumericInput value={data.otherOpEx} onChange={v => onChange({ otherOpEx: v })} prefix="$" testId="input-pl-other-opex" />
        </FieldRow>
        <CustomLineItemsSection
          section="pl-opex"
          items={data.customLineItems}
          onChange={items => onChange({ customLineItems: items })}
        />
      </div>
      {totalOpEx > 0 && (
        <div className="p-4 rounded-xl bg-chart-1/8 border border-chart-1/20">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{t("financial:pl.totalOperatingExpenses")}</span>
            <span className="text-sm font-bold tabular-nums">{fmt$(totalOpEx)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PLStep5BHPH({ data, onChange }: { data: PLData; onChange: (d: Partial<PLData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const bhphNetIncome = n(data.bhphPaymentIncome) + n(data.bhphReserveIncome) - n(data.bhphReposCosts);
  const hasBHPHValues = n(data.bhphPaymentIncome) > 0 || n(data.bhphReposCosts) > 0 || n(data.bhphReserveIncome) > 0;
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2.5 mb-5 p-4 rounded-xl bg-amber-500/8 border border-amber-500/20">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground leading-relaxed">
          <strong>{t("financial:pl.step5InfoBold")}</strong>{t("financial:pl.step5InfoText")}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.bhphPaymentIncome")} hint={t("financial:hints.bhphPaymentIncome")} optional>
          <NumericInput value={data.bhphPaymentIncome} onChange={v => onChange({ bhphPaymentIncome: v })} prefix="$" testId="input-pl-bhph-payment-income" />
        </FieldRow>
        <FieldRow label={t("financial:fields.bhphReserveIncome")} hint={t("financial:hints.bhphReserveIncome")} optional>
          <NumericInput value={data.bhphReserveIncome} onChange={v => onChange({ bhphReserveIncome: v })} prefix="$" testId="input-pl-bhph-reserve" />
        </FieldRow>
        <FieldRow label={t("financial:fields.bhphReposCosts")} hint={t("financial:hints.bhphReposCosts")} optional>
          <NumericInput value={data.bhphReposCosts} onChange={v => onChange({ bhphReposCosts: v })} prefix="$" testId="input-pl-bhph-repos" />
        </FieldRow>
        <CustomLineItemsSection
          section="pl-bhph"
          items={data.customLineItems}
          onChange={items => onChange({ customLineItems: items })}
        />
      </div>
      {hasBHPHValues && (
        <div className="p-4 rounded-xl bg-amber-500/8 border border-amber-500/20">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{t("financial:pl.bhphNetIncome")}</span>
            <span className={`text-sm font-bold tabular-nums ${bhphNetIncome >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
              {fmt$(bhphNetIncome)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{t("financial:pl.bhphNetIncomeFormula")}</p>
        </div>
      )}
    </div>
  );
}

function PLStep6Floorplan({ data, onChange }: { data: PLData; onChange: (d: Partial<PLData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const totalFloor = n(data.floorplanInterest) + n(data.floorplanFees);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:pl.step6Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.floorplanInterest")} hint={t("financial:hints.floorplanInterest")}>
          <NumericInput value={data.floorplanInterest} onChange={v => onChange({ floorplanInterest: v })} prefix="$" testId="input-pl-floorplan-interest" />
        </FieldRow>
        <FieldRow label={t("financial:fields.floorplanFees")} hint={t("financial:hints.floorplanFees")}>
          <NumericInput value={data.floorplanFees} onChange={v => onChange({ floorplanFees: v })} prefix="$" testId="input-pl-floorplan-fees" />
        </FieldRow>
        <FieldRow label={t("financial:fields.reconCosts")} hint={t("financial:hints.reconCosts")}>
          <NumericInput value={data.reconCosts} onChange={v => onChange({ reconCosts: v })} prefix="$" testId="input-pl-recon-costs" />
        </FieldRow>
        <FieldRow label={t("financial:fields.transportCosts")} hint={t("financial:hints.transportCosts")}>
          <NumericInput value={data.transportCosts} onChange={v => onChange({ transportCosts: v })} prefix="$" testId="input-pl-transport-costs" />
        </FieldRow>
        <CustomLineItemsSection
          section="pl-floorplan"
          items={data.customLineItems}
          onChange={items => onChange({ customLineItems: items })}
        />
      </div>
      {totalFloor > 0 && (
        <div className="p-4 rounded-xl bg-chart-2/8 border border-chart-2/20">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{t("financial:pl.totalFloorplanCost")}</span>
            <span className="text-sm font-bold tabular-nums">{fmt$(totalFloor)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PLStep7Review({ data }: { data: PLData }) {
  // Include BHPH payment income + reserve income in the total revenue for display
  const rev = n(data.totalRevenue) + n(data.bhphPaymentIncome) + n(data.bhphReserveIncome);
  const cogs = n(data.costOfGoodsSold);
  const gp = n(data.grossProfit) || (rev - cogs);
  const opEx = n(data.payroll) + n(data.rentMortgage) + n(data.utilities) + n(data.advertising) + n(data.insurance) + n(data.dmsFees) + n(data.dealerPrincipalComp) + n(data.otherOpEx)
    + data.customLineItems.filter(i => i.section === "pl-opex").reduce((s, i) => s + n(i.amount), 0);
  const floorTotal = n(data.floorplanInterest) + n(data.floorplanFees)
    + data.customLineItems.filter(i => i.section === "pl-floorplan").reduce((s, i) => s + n(i.amount), 0);
  const otherCosts = n(data.reconCosts) + n(data.transportCosts);
  const bhphCosts = n(data.bhphReposCosts)
    + data.customLineItems.filter(i => i.section === "pl-bhph").reduce((s, i) => s + n(i.amount), 0);
  const totalExp = opEx + floorTotal + otherCosts + bhphCosts;
  const noi = gp - totalExp;
  const ratios = computePLRatios(data);
  const units = n(data.unitsSold);

  const sectionStyle = "rounded-xl border bg-card p-4 space-y-1";
  const { t } = useTranslation(["financial", "common"]);

  const hasBHPH = n(data.bhphPaymentIncome) > 0 || n(data.bhphReposCosts) > 0 || n(data.bhphReserveIncome) > 0;
  const customRevItems = data.customLineItems.filter(i => i.section === "pl-revenue");
  const customCogsItems = data.customLineItems.filter(i => i.section === "pl-cogs");
  const customOpexItems = data.customLineItems.filter(i => i.section === "pl-opex");
  const customFloorItems = data.customLineItems.filter(i => i.section === "pl-floorplan");
  const customBHPHItems = data.customLineItems.filter(i => i.section === "pl-bhph");
  const hasData = rev > 0 || cogs > 0 || totalExp > 0;

  return (
    <div className="space-y-4">
      <TipBox text={t("financial:pl.step7Tip")} />
      {!hasData && (
        <div className="rounded-xl border bg-muted/40 px-6 py-10 text-center text-sm text-muted-foreground" data-testid="pl-review-empty-prompt">
          {t("financial:pl.emptyReview")}
        </div>
      )}
      <div className={sectionStyle}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.periodDealership")}</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <span className="text-muted-foreground">{t("financial:review.dealership")}</span><span className="font-medium">{data.dealershipName || "—"}</span>
          <span className="text-muted-foreground">{t("financial:review.address")}</span><span className="font-medium">{data.address || "—"}</span>
          <span className="text-muted-foreground">{t("financial:review.state")}</span><span className="font-medium">{data.businessState || "—"}</span>
          <span className="text-muted-foreground">{t("financial:review.period")}</span><span className="font-medium">{data.periodStart && data.periodEnd ? `${fmtDate(data.periodStart)} – ${fmtDate(data.periodEnd)}` : data.periodStart ? fmtDate(data.periodStart) : data.periodEnd ? fmtDate(data.periodEnd) : "—"}</span>
        </div>
      </div>

      {hasData && (
        <>
          <div className={sectionStyle}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.revenueBreakdown")}</p>
            <div className="divide-y divide-border/50">
              {[
                n(data.newVehicleRevenue) > 0 ? [t("financial:review.newVehicleRevenue"), fmt$(n(data.newVehicleRevenue))] : null,
                [t("financial:review.usedRetailRevenue"), n(data.usedRetailRevenue) > 0 ? fmt$(n(data.usedRetailRevenue)) : "—"],
                n(data.wholesaleRevenue) > 0 ? [t("financial:review.wholesaleRevenue"), fmt$(n(data.wholesaleRevenue))] : null,
                [t("financial:review.fiIncome"), n(data.fiRevenue) > 0 ? fmt$(n(data.fiRevenue)) : "—"],
                n(data.docFeesCollected) > 0 ? [t("financial:review.docFeesCollected"), fmt$(n(data.docFeesCollected))] : null,
                n(data.servicePartsRevenue) > 0 ? [t("financial:review.serviceParts"), fmt$(n(data.servicePartsRevenue))] : null,
                n(data.bhphPaymentIncome) > 0 ? [t("financial:review.bhphPaymentIncome"), fmt$(n(data.bhphPaymentIncome))] : null,
                ...customRevItems.map(i => [i.label || t("financial:common.custom"), n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
                [t("financial:review.totalRevenue"), rev > 0 ? fmt$(rev) : "—"],
                [t("financial:review.unitsSold"), units > 0 ? String(units) : "—"],
              ].filter((x): x is string[] => x !== null).map(([l, v]) => (
                <div key={l} className={`flex justify-between py-1.5 text-sm ${l === t("financial:review.totalRevenue") ? "font-semibold" : ""}`}>
                  <span className={l === t("financial:review.totalRevenue") ? "" : "text-muted-foreground"}>{l}</span>
                  <span className="font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={sectionStyle}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.cogs")}</p>
            <div className="divide-y divide-border/50">
              {[
                n(data.newVehicleCogs) > 0 ? [t("financial:review.newVehicleCosts"), fmt$(n(data.newVehicleCogs))] : null,
                [t("financial:review.usedRetailCogs"), n(data.usedRetailCogs) > 0 ? fmt$(n(data.usedRetailCogs)) : "—"],
                n(data.wholesaleCogs) > 0 ? [t("financial:review.wholesaleCogs"), fmt$(n(data.wholesaleCogs))] : null,
                n(data.packPerUnit) > 0 ? [t("financial:review.packPerUnit", { units }), fmt$(n(data.packPerUnit) * units)] : null,
                n(data.auctionFees) > 0 ? [t("financial:review.auctionFees"), fmt$(n(data.auctionFees))] : null,
                n(data.otherCogs) > 0 ? [t("financial:review.otherCogs"), fmt$(n(data.otherCogs))] : null,
                ...customCogsItems.map(i => [i.label || t("financial:common.custom"), n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
                [t("financial:review.totalCogs"), cogs > 0 ? fmt$(cogs) : "—"],
                [t("financial:review.grossProfit"), gp > 0 ? fmt$(gp) : "—"],
              ].filter((x): x is string[] => x !== null).map(([l, v]) => (
                <div key={l} className={`flex justify-between py-1.5 text-sm ${l === t("financial:review.grossProfit") || l === t("financial:review.totalCogs") ? "font-semibold" : ""}`}>
                  <span className={l === t("financial:review.grossProfit") || l === t("financial:review.totalCogs") ? "" : "text-muted-foreground"}>{l}</span>
                  <span className={l === t("financial:review.grossProfit") ? "text-emerald-600 dark:text-emerald-400" : ""}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={sectionStyle}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.operatingExpenses")}</p>
            <div className="divide-y divide-border/50">
              {[
                [t("financial:review.payroll"), n(data.payroll) > 0 ? fmt$(n(data.payroll)) : "—"],
                [t("financial:review.rentMortgage"), n(data.rentMortgage) > 0 ? fmt$(n(data.rentMortgage)) : "—"],
                [t("financial:review.utilities"), n(data.utilities) > 0 ? fmt$(n(data.utilities)) : "—"],
                [t("financial:review.advertising"), n(data.advertising) > 0 ? fmt$(n(data.advertising)) : "—"],
                [t("financial:review.insurance"), n(data.insurance) > 0 ? fmt$(n(data.insurance)) : "—"],
                n(data.dmsFees) > 0 ? [t("financial:review.dmsFees"), fmt$(n(data.dmsFees))] : null,
                n(data.dealerPrincipalComp) > 0 ? [t("financial:review.dealerPrincipalComp"), fmt$(n(data.dealerPrincipalComp))] : null,
                n(data.otherOpEx) > 0 ? [t("financial:review.otherOpEx"), fmt$(n(data.otherOpEx))] : null,
                ...customOpexItems.map(i => [i.label || t("financial:common.custom"), n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
                [t("financial:review.totalOpEx"), opEx > 0 ? fmt$(opEx) : "—"],
              ].filter((x): x is string[] => x !== null).map(([l, v]) => (
                <div key={l} className={`flex justify-between py-1.5 text-sm ${l === t("financial:review.totalOpEx") ? "font-semibold" : ""}`}>
                  <span className={l === t("financial:review.totalOpEx") ? "" : "text-muted-foreground"}>{l}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {hasBHPH && (
            <div className={sectionStyle}>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.bhph")}</p>
              <div className="divide-y divide-border/50">
                {[
                  n(data.bhphReposCosts) > 0 ? [t("financial:review.repoCosts"), fmt$(n(data.bhphReposCosts))] : null,
                  n(data.bhphReserveIncome) > 0 ? [t("financial:review.reserveIncome"), fmt$(n(data.bhphReserveIncome))] : null,
                  ...customBHPHItems.map(i => [i.label || t("financial:common.custom"), n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
                ].filter((x): x is string[] => x !== null).map(([l, v]) => (
                  <div key={l} className="flex justify-between py-1.5 text-sm">
                    <span className="text-muted-foreground">{l}</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={sectionStyle}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.floorplanHolding")}</p>
            <div className="divide-y divide-border/50">
              {[
                [t("financial:review.floorplanInterest"), n(data.floorplanInterest) > 0 ? fmt$(n(data.floorplanInterest)) : "—"],
                [t("financial:review.floorplanFees"), n(data.floorplanFees) > 0 ? fmt$(n(data.floorplanFees)) : "—"],
                [t("financial:review.reconCosts"), n(data.reconCosts) > 0 ? fmt$(n(data.reconCosts)) : "—"],
                [t("financial:review.transportAuction"), n(data.transportCosts) > 0 ? fmt$(n(data.transportCosts)) : "—"],
                ...customFloorItems.map(i => [i.label || t("financial:common.custom"), n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
                [t("financial:review.totalFloorplanHolding"), floorTotal + otherCosts > 0 ? fmt$(floorTotal + otherCosts) : "—"],
              ].filter((x): x is string[] => x !== null).map(([l, v]) => (
                <div key={l} className={`flex justify-between py-1.5 text-sm ${l === t("financial:review.totalFloorplanHolding") ? "font-semibold" : ""}`}>
                  <span className={l === t("financial:review.totalFloorplanHolding") ? "" : "text-muted-foreground"}>{l}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`${sectionStyle} border-primary/20 bg-primary/3`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">{t("financial:review.noi")}</span>
              <span className={`text-lg font-bold tabular-nums ${noi >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                {gp > 0 ? fmt$(noi) : "—"}
              </span>
            </div>
            {ratios.netMarginPct !== null && (
              <p className="text-xs text-muted-foreground mt-1">{t("financial:review.netMargin", { value: fmtPct(ratios.netMarginPct) })}</p>
            )}
          </div>

          {ratios.grossMarginPct !== null && (
            <div className={sectionStyle}>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.kpiSummary")}</p>
              <div className="divide-y divide-border/50">
                {([
                  [t("financial:review.grossMargin"), ratios.grossMarginPct !== null ? fmtPct(ratios.grossMarginPct) : "—"],
                  [t("financial:review.totalExpenseRatio"), ratios.totalExpenseRatio !== null ? fmtPct(ratios.totalExpenseRatio) : "—"],
                  [t("financial:review.floorplanPctGross"), ratios.floorplanAsPctGross !== null ? fmtPct(ratios.floorplanAsPctGross) : "—"],
                  [t("financial:review.floorplanPctRevenue"), ratios.floorplanAsPctRevenue !== null ? fmtPct(ratios.floorplanAsPctRevenue) : "—"],
                  [t("financial:review.pvr"), ratios.pvr !== null ? fmt$(ratios.pvr, 0) : "—"],
                  [t("financial:review.grossProfitPerUnit"), ratios.grossProfitPerUnit !== null ? fmt$(ratios.grossProfitPerUnit, 0) : "—"],
                  ...(hasBHPH && ratios.bhphIncomeAsPctRevenue !== null ? [[t("financial:review.bhphIncomePct"), fmtPct(ratios.bhphIncomeAsPctRevenue)]] : []),
                ] as [string, string][]).map(([l, v]) => (
                  <div key={l} className="flex justify-between py-1.5 text-sm">
                    <span className="text-muted-foreground">{l}</span>
                    <span className="font-medium tabular-nums">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Balance Sheet Wizard Steps ───────────────────────────────────────────────

function BSStep1({ data, onChange }: { data: BSFormData; onChange: (d: Partial<BSFormData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:bs.step1Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:bs.datePrecision")}>
          <select
            value={data.periodPrecision}
            onChange={e => onChange({ periodPrecision: e.target.value as BSPeriodPrecision })}
            data-testid="select-bs-period-precision"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="exact">{t("financial:bs.exactAsOfDate")}</option>
            <option value="year_only">{t("financial:bs.yearOnly")}</option>
          </select>
        </FieldRow>
        {data.periodPrecision === "exact" ? (
          <FieldRow label={t("financial:bs.asOfDate")}>
            <Input type="date" value={data.asOfDate} onChange={e => onChange({ asOfDate: e.target.value })} data-testid="input-bs-as-of-date" />
          </FieldRow>
        ) : (
          <FieldRow label="Year">
            <Input
              type="number"
              min="1900"
              max="9999"
              step="1"
              value={data.periodYear}
              onChange={e => onChange({ periodYear: e.target.value })}
              placeholder="YYYY"
              data-testid="input-bs-period-year"
            />
          </FieldRow>
        )}
        <FieldRow label={t("financial:fields.dealershipName")} hint={t("financial:hints.dealershipName")}>
          <Input value={data.dealershipName} onChange={e => onChange({ dealershipName: e.target.value })} placeholder={t("financial:placeholders.dealershipName")} data-testid="input-bs-dealership-name" />
        </FieldRow>
        <FieldRow label={t("financial:fields.address")} optional>
          <Input value={data.address} onChange={e => onChange({ address: e.target.value })} placeholder={t("financial:placeholders.address")} data-testid="input-bs-address" />
        </FieldRow>
        <FieldRow label={t("financial:fields.businessState")} hint={t("financial:hints.businessState")} optional>
          <Input value={data.businessState} onChange={e => onChange({ businessState: e.target.value })} placeholder={t("financial:placeholders.businessState")} data-testid="input-bs-business-state" />
        </FieldRow>
      </div>
      <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-muted/30">
        <div>
          <p className="text-sm font-medium">{t("financial:bs.cpaTitle")}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("financial:bs.cpaDesc")}</p>
        </div>
        <Switch
          checked={data.cpaPrepared}
          onCheckedChange={(v) => onChange({ cpaPrepared: v })}
          data-testid="switch-bs-cpa-prepared"
        />
      </div>
    </div>
  );
}

function BSStep2({ data, onChange }: { data: BSData; onChange: (d: Partial<BSData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const totals = computeBSTotals(data);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:bs.step2Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.cashAndEquivalents")} hint={t("financial:hints.cashAndEquivalents")}>
          <NumericInput value={data.cashAndEquivalents} onChange={v => onChange({ cashAndEquivalents: v })} prefix="$" testId="input-bs-cash" />
        </FieldRow>
        <FieldRow label={t("financial:fields.accountsReceivable")} hint={t("financial:hints.accountsReceivable")} optional>
          <NumericInput value={data.accountsReceivable} onChange={v => onChange({ accountsReceivable: v })} prefix="$" testId="input-bs-ar" />
        </FieldRow>
        <FieldRow label={t("financial:fields.citAsset")} hint={t("financial:hints.citAsset")} optional>
          <NumericInput value={data.citAsset} onChange={v => onChange({ citAsset: v })} prefix="$" testId="input-bs-cit" />
        </FieldRow>
        <FieldRow label={t("financial:fields.holdbackReceivable")} hint={t("financial:hints.holdbackReceivable")} optional>
          <NumericInput value={data.holdbackReceivable} onChange={v => onChange({ holdbackReceivable: v })} prefix="$" testId="input-bs-holdback" />
        </FieldRow>
        <FieldRow label={t("financial:fields.fiReservesReceivable")} hint={t("financial:hints.fiReservesReceivable")} optional>
          <NumericInput value={data.fiReservesReceivable} onChange={v => onChange({ fiReservesReceivable: v })} prefix="$" testId="input-bs-fi-reserves" />
        </FieldRow>
        <FieldRow label={t("financial:fields.retailInventoryValue")} hint={t("financial:hints.retailInventoryValue")}>
          <NumericInput value={data.retailInventoryValue} onChange={v => onChange({ retailInventoryValue: v })} prefix="$" testId="input-bs-retail-inventory" />
        </FieldRow>
        <FieldRow label={t("financial:fields.wholesaleInventoryValue")} hint={t("financial:hints.wholesaleInventoryValue")} optional>
          <NumericInput value={data.wholesaleInventoryValue} onChange={v => onChange({ wholesaleInventoryValue: v })} prefix="$" testId="input-bs-wholesale-inventory" />
        </FieldRow>
        <FieldRow label={t("financial:fields.notesReceivableShortTerm")} hint={t("financial:hints.notesReceivableShortTerm")} optional>
          <NumericInput value={data.notesReceivableShortTerm} onChange={v => onChange({ notesReceivableShortTerm: v })} prefix="$" testId="input-bs-notes-receivable-st" />
        </FieldRow>
        <FieldRow label={t("financial:fields.prepaidExpenses")} hint={t("financial:hints.prepaidExpenses")} optional>
          <NumericInput value={data.prepaidExpenses} onChange={v => onChange({ prepaidExpenses: v })} prefix="$" testId="input-bs-prepaid" />
        </FieldRow>
        <FieldRow label={t("financial:fields.otherCurrentAssets")} hint={t("financial:hints.otherCurrentAssets")} optional>
          <NumericInput value={data.otherCurrentAssets} onChange={v => onChange({ otherCurrentAssets: v })} prefix="$" testId="input-bs-other-current" />
        </FieldRow>
        <CustomLineItemsSection
          section="bs-current-assets"
          items={data.customLineItems}
          onChange={items => onChange({ customLineItems: items })}
        />
      </div>
      {totals.currentAssets > 0 && (
        <div className="p-4 rounded-xl bg-emerald-500/8 border border-emerald-500/20">
          <div className="flex justify-between text-sm font-semibold">
            <span>{t("financial:bs.totalCurrentAssets")}</span>
            <span className="tabular-nums">{fmt$(totals.currentAssets)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function BSStep3({ data, onChange }: { data: BSData; onChange: (d: Partial<BSData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const totals = computeBSTotals(data);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:bs.step3Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.equipmentValue")} hint={t("financial:hints.equipmentValue")} optional>
          <NumericInput value={data.equipmentValue} onChange={v => onChange({ equipmentValue: v })} prefix="$" testId="input-bs-equipment" />
        </FieldRow>
        <FieldRow label={t("financial:fields.realPropertyValue")} hint={t("financial:hints.realPropertyValue")} optional>
          <NumericInput value={data.realPropertyValue} onChange={v => onChange({ realPropertyValue: v })} prefix="$" testId="input-bs-real-property" />
        </FieldRow>
        <FieldRow label={t("financial:fields.otherLongTermAssets")} hint={t("financial:hints.otherLongTermAssets")} optional>
          <NumericInput value={data.otherLongTermAssets} onChange={v => onChange({ otherLongTermAssets: v })} prefix="$" testId="input-bs-other-lt" />
        </FieldRow>
        <CustomLineItemsSection
          section="bs-lt-assets"
          items={data.customLineItems}
          onChange={items => onChange({ customLineItems: items })}
        />
      </div>
      {totals.longTermAssets > 0 && (
        <div className="p-4 rounded-xl bg-emerald-500/8 border border-emerald-500/20">
          <div className="flex justify-between text-sm font-semibold">
            <span>{t("financial:bs.totalLongTermAssets")}</span>
            <span className="tabular-nums">{fmt$(totals.longTermAssets)}</span>
          </div>
        </div>
      )}
      {totals.totalAssets > 0 && (
        <div className="p-4 rounded-xl bg-emerald-600/10 border border-emerald-600/25">
          <div className="flex justify-between text-sm font-bold">
            <span>{t("financial:bs.totalAssets")}</span>
            <span className="tabular-nums">{fmt$(totals.totalAssets)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FloorplanByLenderSection({
  lenders,
  fallbackTotal,
  onChangeLenders,
  onChangeFallback,
}: {
  lenders: FloorplanLender[];
  fallbackTotal: number | "";
  onChangeLenders: (l: FloorplanLender[]) => void;
  onChangeFallback: (v: number | "") => void;
}) {
  const { t } = useTranslation(["financial", "common"]);
  const addLender = () => {
    onChangeLenders([...lenders, { id: makeId(), lenderName: "", balance: "" }]);
  };

  const updateLender = (id: string, patch: Partial<FloorplanLender>) => {
    onChangeLenders(lenders.map(l => l.id === id ? { ...l, ...patch } : l));
  };

  const removeLender = (id: string) => {
    onChangeLenders(lenders.filter(l => l.id !== id));
  };

  const lenderTotal = lenders.reduce((s, l) => s + n(l.balance), 0);

  return (
    <div className="col-span-full space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{t("financial:floorplanLender.title")}</Label>
        <span className="text-xs text-muted-foreground">{t("financial:floorplanLender.subtitle")}</span>
      </div>
      {lenders.length === 0 ? (
        <FieldRow label="" hint="">
          <NumericInput value={fallbackTotal} onChange={onChangeFallback} prefix="$" testId="input-bs-floorplan-payable" />
        </FieldRow>
      ) : (
        <div className="space-y-2">
          {lenders.map(lender => (
            <div key={lender.id} className="flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  value={lender.lenderName}
                  onChange={e => updateLender(lender.id, { lenderName: e.target.value })}
                  placeholder={t("financial:floorplanLender.namePlaceholder")}
                  className="text-sm"
                  data-testid={`input-lender-name-${lender.id}`}
                />
              </div>
              <div className="w-36">
                <NumericInput
                  value={lender.balance}
                  onChange={v => updateLender(lender.id, { balance: v })}
                  prefix="$"
                  testId={`input-lender-balance-${lender.id}`}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeLender(lender.id)}
                className="h-9 w-9 text-muted-foreground hover:text-rose-500"
                data-testid={`button-remove-lender-${lender.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          {lenderTotal > 0 && (
            <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/15 text-sm flex justify-between">
              <span className="text-muted-foreground">{t("financial:floorplanLender.total")}</span>
              <span className="font-semibold">{fmt$(lenderTotal)}</span>
            </div>
          )}
        </div>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={addLender}
        className="gap-1.5 text-xs h-8"
        data-testid="button-add-lender"
      >
        <Plus className="w-3 h-3" /> {t("financial:floorplanLender.add")}
      </Button>
    </div>
  );
}

function BSStep4({ data, onChange }: { data: BSData; onChange: (d: Partial<BSData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const totals = computeBSTotals(data);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:bs.step4Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FloorplanByLenderSection
          lenders={data.floorplanByLender}
          fallbackTotal={data.floorplanPayable}
          onChangeLenders={lenders => onChange({ floorplanByLender: lenders })}
          onChangeFallback={v => onChange({ floorplanPayable: v })}
        />
        <FieldRow label={t("financial:fields.accountsPayable")} hint={t("financial:hints.accountsPayable")} optional>
          <NumericInput value={data.accountsPayable} onChange={v => onChange({ accountsPayable: v })} prefix="$" testId="input-bs-ap" />
        </FieldRow>
        <FieldRow label={t("financial:fields.accruedExpenses")} hint={t("financial:hints.accruedExpenses")} optional>
          <NumericInput value={data.accruedExpenses} onChange={v => onChange({ accruedExpenses: v })} prefix="$" testId="input-bs-accrued" />
        </FieldRow>
        <FieldRow label={t("financial:fields.shortTermNotesPayable")} hint={t("financial:hints.shortTermNotesPayable")} optional>
          <NumericInput value={data.shortTermNotesPayable} onChange={v => onChange({ shortTermNotesPayable: v })} prefix="$" testId="input-bs-st-notes-payable" />
        </FieldRow>
        <FieldRow label={t("financial:fields.currentPortionLongTermDebt")} hint={t("financial:hints.currentPortionLongTermDebt")} optional>
          <NumericInput value={data.currentPortionLongTermDebt} onChange={v => onChange({ currentPortionLongTermDebt: v })} prefix="$" testId="input-bs-cpltd" />
        </FieldRow>
        <FieldRow label={t("financial:fields.otherCurrentLiabilities")} hint={t("financial:hints.otherCurrentLiabilities")} optional>
          <NumericInput value={data.otherCurrentLiabilities} onChange={v => onChange({ otherCurrentLiabilities: v })} prefix="$" testId="input-bs-other-current-liab" />
        </FieldRow>
        <CustomLineItemsSection
          section="bs-current-liab"
          items={data.customLineItems}
          onChange={items => onChange({ customLineItems: items })}
        />
      </div>
      {totals.currentLiabilities > 0 && (
        <div className="p-4 rounded-xl bg-rose-500/8 border border-rose-500/20">
          <div className="flex justify-between text-sm font-semibold">
            <span>{t("financial:bs.totalCurrentLiabilities")}</span>
            <span className="tabular-nums">{fmt$(totals.currentLiabilities)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function BSStep5({ data, onChange }: { data: BSData; onChange: (d: Partial<BSData>) => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const totals = computeBSTotals(data);
  return (
    <div className="space-y-5">
      <TipBox text={t("financial:bs.step5Tip")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.realEstateLoanPayable")} hint={t("financial:hints.realEstateLoanPayable")} optional>
          <NumericInput value={data.realEstateLoanPayable} onChange={v => onChange({ realEstateLoanPayable: v })} prefix="$" testId="input-bs-real-estate-loan" />
        </FieldRow>
        <FieldRow label={t("financial:fields.equipmentLoanPayable")} hint={t("financial:hints.equipmentLoanPayable")} optional>
          <NumericInput value={data.equipmentLoanPayable} onChange={v => onChange({ equipmentLoanPayable: v })} prefix="$" testId="input-bs-equipment-loan" />
        </FieldRow>
        <FieldRow label={t("financial:fields.longTermDebt")} hint={t("financial:hints.longTermDebt")} optional>
          <NumericInput value={data.longTermDebt} onChange={v => onChange({ longTermDebt: v })} prefix="$" testId="input-bs-lt-debt" />
        </FieldRow>
        <FieldRow label={t("financial:fields.notesPayable")} hint={t("financial:hints.notesPayable")} optional>
          <NumericInput value={data.notesPayable} onChange={v => onChange({ notesPayable: v })} prefix="$" testId="input-bs-notes-payable" />
        </FieldRow>
        <CustomLineItemsSection
          section="bs-lt-liab"
          items={data.customLineItems}
          onChange={items => onChange({ customLineItems: items })}
        />
      </div>
      {totals.longTermLiabilities > 0 && (
        <div className="p-4 rounded-xl bg-rose-500/8 border border-rose-500/20">
          <div className="flex justify-between text-sm font-semibold">
            <span>{t("financial:bs.totalLongTermLiabilities")}</span>
            <span className="tabular-nums">{fmt$(totals.longTermLiabilities)}</span>
          </div>
        </div>
      )}
      <Separator />
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("financial:bs.equityHeader")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldRow label={t("financial:fields.contributedCapital")} hint={t("financial:hints.contributedCapital")} optional>
          <NumericInput value={data.contributedCapital} onChange={v => onChange({ contributedCapital: v })} prefix="$" testId="input-bs-contributed-capital" />
        </FieldRow>
        <FieldRow label={t("financial:fields.retainedEarnings")} hint={t("financial:hints.retainedEarnings")} optional>
          <NumericInput value={data.retainedEarnings} onChange={v => onChange({ retainedEarnings: v })} prefix="$" testId="input-bs-retained-earnings" />
        </FieldRow>
      </div>
      {totals.totalAssets > 0 && (
        <div className={`p-4 rounded-xl border ${totals.ownersEquity >= 0 ? "bg-emerald-500/8 border-emerald-500/20" : "bg-rose-500/8 border-rose-500/20"}`}>
          <div className="flex justify-between text-sm font-bold">
            <span>{t("financial:bs.ownersEquityAuto")}</span>
            <span className={`tabular-nums ${totals.ownersEquity >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
              {fmt$(totals.ownersEquity)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{t("financial:bs.ownersEquityFormula")}</p>
        </div>
      )}
    </div>
  );
}

function BSStep6Review({ data }: { data: BSFormData }) {
  const { t } = useTranslation(["financial", "common"]);
  const totals = computeBSTotals(data);
  const sectionStyle = "rounded-xl border bg-card p-4";

  const floorplanByLenderTotal = data.floorplanByLender.length > 0
    ? data.floorplanByLender.reduce((s, l) => s + n(l.balance), 0)
    : n(data.floorplanPayable);

  const customCurrentAssets = data.customLineItems.filter(i => i.section === "bs-current-assets");
  const customLTAssets = data.customLineItems.filter(i => i.section === "bs-lt-assets");
  const customCurrentLiab = data.customLineItems.filter(i => i.section === "bs-current-liab");
  const customLTLiab = data.customLineItems.filter(i => i.section === "bs-lt-liab");

  return (
    <div className="space-y-4">
      <TipBox text="The fundamental accounting equation is Assets = Liabilities + Equity. Review each section carefully — lenders will compare this to your P&L and bank statements to verify consistency." />

      <div className={sectionStyle}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("financial:review.periodDealership")}</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <span className="text-muted-foreground">Dealership</span><span className="font-medium">{data.dealershipName || "—"}</span>
          <span className="text-muted-foreground">Address</span><span className="font-medium">{data.address || "—"}</span>
          <span className="text-muted-foreground">State</span><span className="font-medium">{data.businessState || "—"}</span>
          <span className="text-muted-foreground">{t("financial:bs.balanceSheetDate")}</span>
          <span className="font-medium">{data.periodPrecision === "exact" ? (data.asOfDate ? `As of ${fmtDate(data.asOfDate)}` : "—") : data.periodYear ? `Year-only: ${data.periodYear}` : "—"}</span>
        </div>
      </div>

      <div className={sectionStyle}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Assets</p>
        <div className="divide-y divide-border/50">
          {([
            ["Cash & Equivalents", n(data.cashAndEquivalents) > 0 ? fmt$(n(data.cashAndEquivalents)) : "—"],
            ["Accounts Receivable", n(data.accountsReceivable) > 0 ? fmt$(n(data.accountsReceivable)) : "—"],
            n(data.citAsset) > 0 ? ["CIT (Cash in Transit)", fmt$(n(data.citAsset))] : null,
            n(data.holdbackReceivable) > 0 ? ["Holdback Receivable", fmt$(n(data.holdbackReceivable))] : null,
            n(data.fiReservesReceivable) > 0 ? ["F&I Reserves Receivable", fmt$(n(data.fiReservesReceivable))] : null,
            n(data.retailInventoryValue) > 0 ? ["Retail Inventory", fmt$(n(data.retailInventoryValue))] : null,
            n(data.wholesaleInventoryValue) > 0 ? ["Wholesale Inventory", fmt$(n(data.wholesaleInventoryValue))] : null,
            totals.inventoryValue > 0 ? ["Total Inventory", fmt$(totals.inventoryValue)] : null,
            n(data.prepaidExpenses) > 0 ? ["Prepaid Expenses", fmt$(n(data.prepaidExpenses))] : null,
            n(data.otherCurrentAssets) > 0 ? ["Other Current Assets", fmt$(n(data.otherCurrentAssets))] : null,
            ...customCurrentAssets.map(i => [i.label || "Custom", n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"]),
            ["Total Current Assets", fmt$(totals.currentAssets)],
            n(data.equipmentValue) > 0 ? ["Equipment & Vehicles", fmt$(n(data.equipmentValue))] : null,
            n(data.realPropertyValue) > 0 ? ["Real Property / Real Estate", fmt$(n(data.realPropertyValue))] : null,
            n(data.otherLongTermAssets) > 0 ? ["Other Long-Term Assets", fmt$(n(data.otherLongTermAssets))] : null,
            ...customLTAssets.map(i => [i.label || "Custom", n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"]),
            ["Total Long-Term Assets", fmt$(totals.longTermAssets)],
            ["TOTAL ASSETS", fmt$(totals.totalAssets)],
          ].filter(Boolean) as [string, string][]).map(([l, v]) => (
            <div key={l} className={`flex justify-between py-1.5 text-sm ${l.startsWith("TOTAL") ? "font-bold" : l.startsWith("Total") ? "font-semibold" : ""}`}>
              <span className={l.startsWith("TOTAL") || l.startsWith("Total") ? "" : "text-muted-foreground"}>{l}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={sectionStyle}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Liabilities</p>
        <div className="divide-y divide-border/50">
          {([
            data.floorplanByLender.length > 0
              ? data.floorplanByLender.map(l => [`  ${l.lenderName || "Floorplan Lender"}`, n(l.balance) > 0 ? fmt$(n(l.balance)) : "—"])
              : [["Floorplan Payable", n(data.floorplanPayable) > 0 ? fmt$(n(data.floorplanPayable)) : "—"]],
            data.floorplanByLender.length > 0 ? [["Floorplan Payable (Total)", floorplanByLenderTotal > 0 ? fmt$(floorplanByLenderTotal) : "—"]] : [],
            n(data.accountsPayable) > 0 ? [["Accounts Payable", fmt$(n(data.accountsPayable))]] : [],
            n(data.accruedExpenses) > 0 ? [["Accrued Expenses", fmt$(n(data.accruedExpenses))]] : [],
            n(data.otherCurrentLiabilities) > 0 ? [["Other Current Liabilities", fmt$(n(data.otherCurrentLiabilities))]] : [],
            customCurrentLiab.map(i => [i.label || "Custom", n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"]),
            [["Total Current Liabilities", fmt$(totals.currentLiabilities)]],
            n(data.realEstateLoanPayable) > 0 ? [["Real Estate Loan / Mortgage", fmt$(n(data.realEstateLoanPayable))]] : [],
            n(data.equipmentLoanPayable) > 0 ? [["Equipment Loan", fmt$(n(data.equipmentLoanPayable))]] : [],
            n(data.longTermDebt) > 0 ? [["Long-Term Debt (Other)", fmt$(n(data.longTermDebt))]] : [],
            n(data.notesPayable) > 0 ? [["Notes Payable (Other)", fmt$(n(data.notesPayable))]] : [],
            customLTLiab.map(i => [i.label || "Custom", n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"]),
            [["Total Long-Term Liabilities", fmt$(totals.longTermLiabilities)]],
            [["TOTAL LIABILITIES", fmt$(totals.totalLiabilities)]],
          ].flat() as [string, string][]).map(([l, v]) => (
            <div key={l} className={`flex justify-between py-1.5 text-sm ${l.startsWith("TOTAL") ? "font-bold" : l.startsWith("Total") ? "font-semibold" : ""}`}>
              <span className={l.startsWith("TOTAL") || l.startsWith("Total") ? "" : "text-muted-foreground"}>{l}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={`${sectionStyle} border-primary/20 bg-primary/3`}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Equity</p>
        <div className="divide-y divide-border/50">
          {([
            ["Contributed Capital", n(data.contributedCapital) > 0 ? fmt$(n(data.contributedCapital)) : "—"],
            ["Retained Earnings", fmt$(n(data.retainedEarnings))],
            ["Owner's Equity (Calculated)", fmt$(totals.ownersEquity)],
          ] as [string, string][]).map(([l, v]) => (
            <div key={l} className={`flex justify-between py-1.5 text-sm ${l.startsWith("Owner") ? "font-semibold" : ""}`}>
              <span className={l.startsWith("Owner") ? "" : "text-muted-foreground"}>{l}</span>
              <span className={l.startsWith("Owner") ? (totals.ownersEquity >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400") : ""}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={`p-4 rounded-xl border ${totals.totalAssets === 0 ? "border-muted bg-muted/30" : totals.isBalanced ? "border-emerald-500/25 bg-emerald-500/5" : "border-amber-500/25 bg-amber-500/5"}`}>
        <div className="flex items-center justify-between text-sm font-bold">
          <span>{t("financial:review.totalLiabEquityCaps")}</span>
          <span>{fmt$(totals.totalLiabilities + totals.contributedCapital + totals.retainedEarnings + totals.ownersEquity)}</span>
        </div>
        <p className={`text-xs mt-1 ${totals.totalAssets === 0 ? "text-muted-foreground" : totals.isBalanced ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
          {totals.totalAssets === 0 ? "Enter your asset and liability figures above" : totals.isBalanced ? "✓ Balance sheet is balanced" : "⚠ Verify your entries — assets and liabilities + equity don't match"}
        </p>
      </div>
    </div>
  );
}

// ─── Wizard Config ─────────────────────────────────────────────────────────────

const PL_STEPS = [
  { label: "Dealer & Period Info", short: "Info" },
  { label: "Revenue Breakdown", short: "Revenue" },
  { label: "Cost of Goods Sold", short: "COGS" },
  { label: "Operating Expenses", short: "Expenses" },
  { label: "BHPH / In-House Financing", short: "BHPH" },
  { label: "Floorplan & Holding", short: "Floorplan" },
  { label: "Review & KPIs", short: "Review" },
];

const BS_STEPS = [
  { label: "Dealer & Period Info", short: "Info" },
  { label: "Current Assets", short: "Curr. Assets" },
  { label: "Long-Term Assets", short: "LT Assets" },
  { label: "Current Liabilities", short: "Curr. Liab." },
  { label: "LT Liabilities & Equity", short: "LT Liab." },
  { label: "Review", short: "Review" },
];

const defaultPLData = (): PLData => ({
  periodStart: "",
  periodEnd: "",
  dealershipName: "",
  address: "",
  businessState: "",
  unitsSold: "",
  newVehicleRevenue: "",
  usedRetailRevenue: "",
  wholesaleRevenue: "",
  fiRevenue: "",
  docFeesCollected: "",
  servicePartsRevenue: "",
  bhphPaymentIncome: "",
  totalRevenue: "",
  newVehicleCogs: "",
  usedRetailCogs: "",
  wholesaleCogs: "",
  packPerUnit: "",
  auctionFees: "",
  otherCogs: "",
  costOfGoodsSold: "",
  grossProfit: "",
  avgFrontEndGross: "",
  avgBackEndGross: "",
  payroll: "",
  rentMortgage: "",
  utilities: "",
  advertising: "",
  insurance: "",
  dmsFees: "",
  dealerPrincipalComp: "",
  otherOpEx: "",
  bhphReposCosts: "",
  bhphReserveIncome: "",
  floorplanInterest: "",
  floorplanFees: "",
  reconCosts: "",
  transportCosts: "",
  customLineItems: [],
});

const defaultBSData = (): BSData => ({
  periodStart: "",
  periodEnd: "",
  dealershipName: "",
  address: "",
  businessState: "",
  cpaPrepared: false,
  cashAndEquivalents: "",
  accountsReceivable: "",
  notesReceivableShortTerm: "",
  citAsset: "",
  holdbackReceivable: "",
  fiReservesReceivable: "",
  retailInventoryValue: "",
  wholesaleInventoryValue: "",
  prepaidExpenses: "",
  otherCurrentAssets: "",
  equipmentValue: "",
  realPropertyValue: "",
  otherLongTermAssets: "",
  floorplanPayable: "",
  floorplanByLender: [],
  accountsPayable: "",
  accruedExpenses: "",
  shortTermNotesPayable: "",
  currentPortionLongTermDebt: "",
  otherCurrentLiabilities: "",
  realEstateLoanPayable: "",
  equipmentLoanPayable: "",
  longTermDebt: "",
  notesPayable: "",
  contributedCapital: "",
  retainedEarnings: "",
  customLineItems: [],
});

function buildBSFormDataFromStatement(stmt: FinancialStatement): BSFormData {
  const data = buildBSDataFromStatement(stmt);
  return {
    ...data,
    asOfDate: stmt.asOfDate ?? "",
    periodYear: stmt.periodYear != null ? String(stmt.periodYear) : "",
    periodPrecision: stmt.periodPrecision === "year_only" ? "year_only" : "exact",
  };
}

// ─── Generic Wizard UI ────────────────────────────────────────────────────────

function WizardHeader({ steps, step, onCancel }: { steps: { label: string; short: string }[]; step: number; onCancel: () => void }) {
  const progress = ((step + 1) / steps.length) * 100;
  return (
    <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
      <div className="max-w-5xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs text-muted-foreground">Step {step + 1} of {steps.length}</p>
            <p className="text-sm font-semibold">{steps[step].label}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="button-cancel-wizard">
            Cancel
          </Button>
        </div>
        <Progress value={progress} className="h-1.5" data-testid="wizard-progress" />
        <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
          {steps.map((s, i) => (
            <button
              key={i}
              disabled={i >= step}
              className={`shrink-0 text-xs px-2 py-1 rounded-full transition-all font-medium ${
                i === step
                  ? "bg-primary text-primary-foreground cursor-default shadow-sm"
                  : i < step
                  ? "bg-primary/15 text-primary cursor-default"
                  : "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
              }`}
              data-testid={`step-tab-${i}`}
            >
              {i < step && <CheckCircle2 className="w-3 h-3 inline mr-0.5" />}
              {s.short}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Helper: Build body from PLData ──────────────────────────────────────────

function buildPLBody(data: PLData) {
  const rev = n(data.totalRevenue);
  const cogs = n(data.costOfGoodsSold);
  const gp = n(data.grossProfit) || (rev > 0 && cogs > 0 ? rev - cogs : 0);
  const opEx = n(data.payroll) + n(data.rentMortgage) + n(data.utilities) + n(data.advertising) + n(data.insurance) + n(data.dmsFees) + n(data.dealerPrincipalComp) + n(data.otherOpEx);
  return {
    statementType: "pl" as const,
    periodStart: data.periodStart,
    periodEnd: data.periodEnd,
    dealershipName: data.dealershipName || null,
    address: data.address || null,
    businessState: data.businessState || null,
    unitsSold: n(data.unitsSold) || null,
    newVehicleRevenue: n(data.newVehicleRevenue) || null,
    usedRetailRevenue: n(data.usedRetailRevenue) || null,
    wholesaleRevenue: n(data.wholesaleRevenue) || null,
    fiRevenue: n(data.fiRevenue) || null,
    docFeesCollected: n(data.docFeesCollected) || null,
    servicePartsRevenue: n(data.servicePartsRevenue) || null,
    bhphPaymentIncome: n(data.bhphPaymentIncome) || null,
    totalRevenue: rev || null,
    costOfGoodsSold: n(data.costOfGoodsSold) || null,
    grossProfit: gp || null,
    avgFrontEndGross: n(data.avgFrontEndGross) || null,
    avgBackEndGross: n(data.avgBackEndGross) || null,
    totalOperatingExpenses: opEx || null,
    payroll: n(data.payroll) || null,
    rentMortgage: n(data.rentMortgage) || null,
    utilities: n(data.utilities) || null,
    advertising: n(data.advertising) || null,
    insurance: n(data.insurance) || null,
    dmsFees: n(data.dmsFees) || null,
    dealerPrincipalComp: n(data.dealerPrincipalComp) || null,
    otherOpEx: n(data.otherOpEx) || null,
    bhphReposCosts: n(data.bhphReposCosts) || null,
    bhphReserveIncome: n(data.bhphReserveIncome) || null,
    packPerUnit: n(data.packPerUnit) || null,
    auctionFees: n(data.auctionFees) || null,
    floorplanInterest: n(data.floorplanInterest) || null,
    floorplanFees: n(data.floorplanFees) || null,
    reconCosts: n(data.reconCosts) || null,
    transportCosts: n(data.transportCosts) || null,
    customFieldsJson: data.customLineItems.length > 0 ? JSON.stringify(data.customLineItems) : null,
    ratiosJson: JSON.stringify(computePLRatios(data)),
    status: "completed",
  };
}

function buildBSBody(data: BSFormData) {
  const totals = computeBSTotals(data);
  const floorplanByLenderTotal = data.floorplanByLender.length > 0
    ? data.floorplanByLender.reduce((s, l) => s + n(l.balance), 0)
    : n(data.floorplanPayable);
  return {
    statementType: "balance_sheet" as const,
    // Balance sheets are point-in-time statements, not activity periods.
    // Keep the legacy raw range columns empty rather than synthesizing dates.
    periodStart: "",
    periodEnd: "",
    asOfDate: data.periodPrecision === "exact" ? data.asOfDate || null : null,
    periodYear: data.periodPrecision === "year_only" && data.periodYear ? Number(data.periodYear) : null,
    dealershipName: data.dealershipName || null,
    address: data.address || null,
    businessState: data.businessState || null,
    cpaPrepared: data.cpaPrepared ?? false,
    cashAndEquivalents: n(data.cashAndEquivalents) || null,
    accountsReceivable: n(data.accountsReceivable) || null,
    notesReceivableShortTerm: n(data.notesReceivableShortTerm) || null,
    citAsset: n(data.citAsset) || null,
    holdbackReceivable: n(data.holdbackReceivable) || null,
    fiReservesReceivable: n(data.fiReservesReceivable) || null,
    retailInventoryValue: n(data.retailInventoryValue) || null,
    wholesaleInventoryValue: n(data.wholesaleInventoryValue) || null,
    inventoryValue: totals.inventoryValue || null,
    prepaidExpenses: n(data.prepaidExpenses) || null,
    otherCurrentAssets: n(data.otherCurrentAssets) || null,
    equipmentValue: n(data.equipmentValue) || null,
    realPropertyValue: n(data.realPropertyValue) || null,
    otherLongTermAssets: n(data.otherLongTermAssets) || null,
    totalAssets: totals.totalAssets || null,
    floorplanPayable: floorplanByLenderTotal || null,
    accountsPayable: n(data.accountsPayable) || null,
    accruedExpenses: n(data.accruedExpenses) || null,
    shortTermNotesPayable: n(data.shortTermNotesPayable) || null,
    currentPortionLongTermDebt: n(data.currentPortionLongTermDebt) || null,
    otherCurrentLiabilities: n(data.otherCurrentLiabilities) || null,
    realEstateLoanPayable: n(data.realEstateLoanPayable) || null,
    equipmentLoanPayable: n(data.equipmentLoanPayable) || null,
    longTermDebt: n(data.longTermDebt) || null,
    notesPayable: n(data.notesPayable) || null,
    totalLiabilities: totals.totalLiabilities || null,
    contributedCapital: n(data.contributedCapital) || null,
    retainedEarnings: n(data.retainedEarnings) || null,
    floorplanByLenderJson: data.floorplanByLender.length > 0 ? JSON.stringify(data.floorplanByLender) : null,
    customFieldsJson: data.customLineItems.length > 0 ? JSON.stringify(data.customLineItems) : null,
    status: "completed",
  };
}

// ─── Statement List Card ──────────────────────────────────────────────────────

function StatementCard({ stmt, onView, onExport, onEdit, onDelete }: {
  stmt: FinancialStatement;
  onView: (s: FinancialStatement) => void;
  onExport: (s: FinancialStatement) => void;
  onEdit: (s: FinancialStatement) => void;
  onDelete: (s: FinancialStatement) => void;
}) {
  const { t } = useTranslation(["financial", "common"]);
  const isPL = (stmt.statementType ?? "pl") === "pl";
  let ratios: PLRatios | null = null;
  try { ratios = stmt.ratiosJson ? JSON.parse(stmt.ratiosJson) as PLRatios : null; } catch { /* ignore */ }

  return (
    <div className="flex flex-wrap items-center justify-between p-4 rounded-xl border bg-card gap-3" data-testid={`card-statement-${stmt.id}`}>
      <div>
        <div className="flex items-center gap-2">
          <p className="font-semibold" data-testid={`text-stmt-name-${stmt.id}`}>{stmt.dealershipName || "Unnamed Statement"}</p>
          <Badge variant="outline" className="text-xs">{isPL ? "P&L" : "Balance Sheet"}</Badge>
          <Badge variant="outline" className="text-xs" data-testid={`badge-source-${stmt.id}`}>{statementSourceLabel(stmt.sourceType)}</Badge>
        </div>
        <p className="text-sm text-muted-foreground" data-testid={`text-stmt-period-${stmt.id}`}>{isPL ? (stmt.periodStart && stmt.periodEnd ? `${stmt.periodStart} – ${stmt.periodEnd}` : stmt.periodStart || stmt.periodEnd || "—") : bsPeriodLabel(stmt)}</p>
        <p className="text-xs text-muted-foreground mt-0.5">Prepared {new Date(stmt.createdAt!).toLocaleDateString()}</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {isPL && ratios?.grossMarginPct != null && (
          <Badge variant="outline" className="text-xs" data-testid={`badge-gm-${stmt.id}`}>
            GM: {fmtPct(ratios.grossMarginPct)}
          </Badge>
        )}
        {isPL && stmt.totalRevenue && (
          <Badge variant="secondary" className="text-xs" data-testid={`badge-rev-${stmt.id}`}>
            Rev: {fmt$(parseFloat(String(stmt.totalRevenue)))}
          </Badge>
        )}
        {!isPL && stmt.totalAssets && (
          <Badge variant="secondary" className="text-xs" data-testid={`badge-assets-${stmt.id}`}>
            Assets: {fmt$(parseFloat(String(stmt.totalAssets)))}
          </Badge>
        )}
        <Button size="sm" variant="outline" onClick={() => onView(stmt)} data-testid={`button-view-${stmt.id}`} className="gap-1.5">
          <Eye className="w-3.5 h-3.5" /> View
        </Button>
        <Button size="sm" variant="outline" onClick={() => onEdit(stmt)} data-testid={`button-edit-${stmt.id}`} className="gap-1.5">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </Button>
        <Button size="sm" variant="outline" onClick={() => onExport(stmt)} data-testid={`button-export-${stmt.id}`} className="gap-1.5">
          <Download className="w-3.5 h-3.5" /> {t("financial:card.exportPdf")}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" data-testid={`button-delete-${stmt.id}`} className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("financial:card.deleteTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("financial:card.deletePrefix")}<strong>{stmt.dealershipName || t("financial:card.deleteThisStatement")}</strong>{t("financial:card.deleteSuffix")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`button-delete-cancel-${stmt.id}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid={`button-delete-confirm-${stmt.id}`}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => onDelete(stmt)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// ─── Detail Views ──────────────────────────────────────────────────────────────

function PLDetailView({ stmt, onBack }: { stmt: FinancialStatement; onBack: () => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const d = buildPLDataFromStatement(stmt);
  const r = computePLRatios(d);
  // Include BHPH payment income + reserve income in displayed total for consistency with review/PDF
  const rev = n(stmt.totalRevenue) + n(stmt.bhphPaymentIncome) + n(stmt.bhphReserveIncome);
  const gp = n(stmt.grossProfit);
  const opEx = n(stmt.payroll) + n(stmt.rentMortgage) + n(stmt.utilities) + n(stmt.advertising) + n(stmt.insurance) + n(stmt.dealerPrincipalComp) + n(stmt.dmsFees) + n(stmt.otherOpEx);
  const floorTotal = n(stmt.floorplanInterest) + n(stmt.floorplanFees);
  const totalExp = opEx + floorTotal + n(stmt.reconCosts) + n(stmt.transportCosts) + n(stmt.bhphReposCosts);
  const noi = gp - totalExp;
  const sc = "p-4 rounded-xl border bg-card mb-3";

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="button-back-to-list" className="gap-2">
          <ChevronLeft className="w-4 h-4" /> Back
        </Button>
        <div className="flex-1" />
        <Button onClick={() => exportPLPDF(d)} data-testid="button-export-detail" className="gap-2" size="sm">
          <Download className="w-3.5 h-3.5" /> {t("financial:detail.exportPdf")}
        </Button>
      </div>
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold" data-testid="heading-detail-name">{stmt.dealershipName || t("financial:detail.plName")}</h2>
          <Badge>{t("financial:detail.plBadge")}</Badge>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="text-pl-detail-period">{stmt.periodStart && stmt.periodEnd ? `${stmt.periodStart} – ${stmt.periodEnd}` : stmt.periodStart || stmt.periodEnd || "—"}</p>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-xs" data-testid={`badge-detail-source-${stmt.id}`}>{statementSourceLabel(stmt.sourceType)}</Badge>
          <p className="text-xs text-muted-foreground">Prepared {new Date(stmt.createdAt!).toLocaleDateString()}</p>
        </div>
      </div>
      <div className={sc}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t("financial:detail.revenueProfit")}</p>
        <div className="divide-y divide-border/50">
          {([
            ["Total Revenue", rev > 0 ? fmt$(rev) : "—"],
            ["Gross Profit", gp > 0 ? fmt$(gp) : "—"],
            ["Units Sold", stmt.unitsSold != null ? String(stmt.unitsSold) : "—"],
          ] as [string, string][]).map(([l, v]) => (
            <div key={l} className="flex justify-between py-1.5 text-sm"><span className="text-muted-foreground">{l}</span><span className="font-medium">{v}</span></div>
          ))}
        </div>
      </div>
      {opEx > 0 && (
        <div className={sc}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t("financial:detail.operatingExpenses")}</p>
          <div className="divide-y divide-border/50">
            {([["Total OpEx", fmt$(opEx)]] as [string, string][]).map(([l, v]) => (
              <div key={l} className="flex justify-between py-1.5 text-sm font-semibold"><span>{l}</span><span>{v}</span></div>
            ))}
          </div>
        </div>
      )}
      <div className={`${sc} border-primary/20 bg-primary/5`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">{t("financial:detail.noi")}</span>
          <span className={`text-lg font-bold tabular-nums ${noi >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
            {gp > 0 ? fmt$(noi) : "—"}
          </span>
        </div>
      </div>
      {r.grossMarginPct !== null && (
        <div className={sc}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t("financial:detail.keyRatios")}</p>
          <div className="divide-y divide-border/50">
            {([
              ["Gross Margin %", fmtPct(r.grossMarginPct)],
              ["Total Expense Ratio", r.totalExpenseRatio !== null ? fmtPct(r.totalExpenseRatio) : "—"],
              ["Net Margin %", r.netMarginPct !== null ? fmtPct(r.netMarginPct) : "—"],
              ["PVR (Front + Back Gross)", r.pvr !== null ? fmt$(r.pvr, 0) : "—"],
              ["Gross Profit / Unit", r.grossProfitPerUnit !== null ? fmt$(r.grossProfitPerUnit, 0) : "—"],
            ] as [string, string][]).map(([l, v]) => (
              <div key={l} className="flex justify-between py-1.5 text-sm"><span className="text-muted-foreground">{l}</span><span className="font-medium">{v}</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BSDetailView({ stmt, onBack }: { stmt: FinancialStatement; onBack: () => void }) {
  const { t } = useTranslation(["financial", "common"]);
  const d = buildBSDataFromStatement(stmt);
  const totals = computeBSTotals(d);
  const sc = "p-4 rounded-xl border bg-card mb-3";

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="button-back-to-list" className="gap-2">
          <ChevronLeft className="w-4 h-4" /> Back
        </Button>
        <div className="flex-1" />
        <Button onClick={() => exportBSPDF(d)} data-testid="button-export-detail" className="gap-2" size="sm">
          <Download className="w-3.5 h-3.5" /> {t("financial:detail.exportPdf")}
        </Button>
      </div>
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold" data-testid="heading-detail-name">{stmt.dealershipName || t("financial:detail.bsName")}</h2>
          <Badge>{t("financial:detail.bsBadge")}</Badge>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="text-bs-detail-period">{bsPeriodLabel(stmt)}</p>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-xs" data-testid={`badge-detail-source-${stmt.id}`}>{statementSourceLabel(stmt.sourceType)}</Badge>
          <p className="text-xs text-muted-foreground">Prepared {new Date(stmt.createdAt!).toLocaleDateString()}</p>
        </div>
      </div>
      <div className={sc}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Assets</p>
        <div className="divide-y divide-border/50">
          {([["Total Current Assets", fmt$(totals.currentAssets)], ["Total Long-Term Assets", fmt$(totals.longTermAssets)], ["Total Assets", fmt$(totals.totalAssets)]] as [string, string][]).map(([l, v]) => (
            <div key={l} className={`flex justify-between py-1.5 text-sm ${l === "Total Assets" ? "font-bold" : "font-semibold"}`}>
              <span>{l}</span><span>{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={sc}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Liabilities</p>
        <div className="divide-y divide-border/50">
          {([["Total Current Liabilities", fmt$(totals.currentLiabilities)], ["Total Long-Term Liabilities", fmt$(totals.longTermLiabilities)], ["Total Liabilities", fmt$(totals.totalLiabilities)]] as [string, string][]).map(([l, v]) => (
            <div key={l} className={`flex justify-between py-1.5 text-sm ${l === "Total Liabilities" ? "font-bold" : "font-semibold"}`}>
              <span>{l}</span><span>{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={`${sc} border-primary/20 bg-primary/5`}>
        <div className="flex justify-between text-sm font-bold">
          <span>{t("financial:detail.ownersEquity")}</span>
          <span className={totals.ownersEquity >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}>
            {fmt$(totals.ownersEquity)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Tab Component ─────────────────────────────────────────────────────────────

function TabButton({ active, onClick, children, testId }: { active: boolean; onClick: () => void; children: React.ReactNode; testId?: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
      }`}
    >
      {children}
    </button>
  );
}

// ─── PL Wizard ────────────────────────────────────────────────────────────────

interface PLWizardProps {
  onClose: () => void;
  editStatement?: FinancialStatement;
}

function PLWizard({ onClose, editStatement }: PLWizardProps) {
  const { t } = useTranslation(["financial", "common"]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<PLData>(() =>
    editStatement ? buildPLDataFromStatement(editStatement) : defaultPLData()
  );

  const update = useCallback((partial: Partial<PLData>) => {
    setData(prev => ({ ...prev, ...partial }));
  }, []);

  const saveMutation = useMutation({
    mutationFn: async (body: ReturnType<typeof buildPLBody>) => {
      if (editStatement) {
        const res = await apiRequest("PATCH", `/api/financial-statements/${editStatement.id}`, body);
        return res.json();
      }
      const res = await apiRequest("POST", "/api/financial-statements", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/financial-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      toast({ title: editStatement ? t("financial:toasts.plUpdated") : t("financial:toasts.plSaved"), description: t("financial:toasts.savedToDocuments") });
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: t("financial:toasts.saveFailed"), description: err?.message ?? t("financial:toasts.genericError"), variant: "destructive" });
    },
  });

  const ratios = computePLRatios(data);

  return (
    <div className="min-h-full">
      <WizardHeader steps={PL_STEPS} step={step} onCancel={onClose} />
      <div className="max-w-5xl mx-auto px-6 py-6 flex gap-6">
        <div className="flex-1 min-w-0">
          <div className="rounded-xl border bg-card p-6">
            {step === 0 && <PLStep1 data={data} onChange={update} />}
            {step === 1 && <PLStep2 data={data} onChange={update} />}
            {step === 2 && <PLStep3 data={data} onChange={update} />}
            {step === 3 && <PLStep4 data={data} onChange={update} />}
            {step === 4 && <PLStep5BHPH data={data} onChange={update} />}
            {step === 5 && <PLStep6Floorplan data={data} onChange={update} />}
            {step === 6 && <PLStep7Review data={data} />}
          </div>
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} data-testid="button-wizard-back" className="gap-2">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            {step < PL_STEPS.length - 1 ? (
              <Button onClick={() => setStep(s => s + 1)} data-testid="button-wizard-next" className="gap-2">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => exportPLPDF(data)} data-testid="button-export-pdf-wizard" className="gap-2">
                  <Download className="w-4 h-4" /> {t("financial:wizard.exportPdf")}
                </Button>
                <Button
                  onClick={() => saveMutation.mutate(buildPLBody(data))}
                  disabled={saveMutation.isPending || !data.periodStart || !data.periodEnd || !data.dealershipName}
                  data-testid="button-save-statement"
                  className="gap-2"
                >
                  {saveMutation.isPending ? "Saving…" : editStatement ? "Update Statement" : "Save to Documents"}
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="w-60 shrink-0 hidden lg:block">
          <div className="sticky top-[120px]">
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" /> {t("financial:wizard.liveKpis")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <RatioIndicator label={t("financial:kpi.grossMarginLabel")} value={ratios.grossMarginPct} format={v => fmtPct(v)} thresholds={{ warn: 15, danger: 8, higherIsBetter: true }} hint={t("financial:kpi.grossMarginHint")} />
                <RatioIndicator label={t("financial:kpi.totalExpenseLabel")} value={ratios.totalExpenseRatio} format={v => fmtPct(v)} thresholds={{ warn: 80, danger: 95 }} hint={t("financial:kpi.totalExpenseHint")} />
                <RatioIndicator label={t("financial:kpi.floorplanGrossLabel")} value={ratios.floorplanAsPctGross} format={v => fmtPct(v)} thresholds={{ warn: 15, danger: 25 }} hint={t("financial:kpi.floorplanGrossHint")} />
                <RatioIndicator label={t("financial:kpi.pvrLabel")} value={ratios.pvr} format={v => fmt$(v, 0)} hint={t("financial:kpi.pvrHint")} />
                <RatioIndicator label={t("financial:kpi.gppuLabel")} value={ratios.grossProfitPerUnit} format={v => fmt$(v, 0)} hint={t("financial:kpi.gppuHint")} />
                <RatioIndicator label={t("financial:kpi.noiLabel")} value={ratios.netOperatingIncome} format={v => fmt$(v, 0)} hint={t("financial:kpi.noiHint")} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── BS Wizard ────────────────────────────────────────────────────────────────

function BSWizard({ onClose, editStatement }: { onClose: () => void; editStatement?: FinancialStatement }) {
  const { t } = useTranslation(["financial", "common"]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<BSFormData>(() =>
    editStatement
      ? buildBSFormDataFromStatement(editStatement)
      : { ...defaultBSData(), asOfDate: "", periodYear: "", periodPrecision: "exact" }
  );

  const update = useCallback((partial: Partial<BSFormData>) => {
    setData(prev => ({ ...prev, ...partial }));
  }, []);

  const totals = computeBSTotals(data);

  const saveMutation = useMutation({
    mutationFn: async (body: ReturnType<typeof buildBSBody>) => {
      if (editStatement) {
        const res = await apiRequest("PATCH", `/api/financial-statements/${editStatement.id}`, body);
        return res.json();
      }
      const res = await apiRequest("POST", "/api/financial-statements", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/financial-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      toast({ title: editStatement ? t("financial:toasts.bsUpdated") : t("financial:toasts.bsSaved"), description: t("financial:toasts.savedToDocuments") });
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: t("financial:toasts.saveFailed"), description: err?.message ?? t("financial:toasts.genericError"), variant: "destructive" });
    },
  });

  return (
    <div className="min-h-full">
      <WizardHeader steps={BS_STEPS} step={step} onCancel={onClose} />
      <div className="max-w-5xl mx-auto px-6 py-6 flex gap-6">
        <div className="flex-1 min-w-0">
          <div className="rounded-xl border bg-card p-6">
            {step === 0 && <BSStep1 data={data} onChange={update} />}
            {step === 1 && <BSStep2 data={data} onChange={update} />}
            {step === 2 && <BSStep3 data={data} onChange={update} />}
            {step === 3 && <BSStep4 data={data} onChange={update} />}
            {step === 4 && <BSStep5 data={data} onChange={update} />}
            {step === 5 && <BSStep6Review data={data} />}
          </div>
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} data-testid="button-bs-wizard-back" className="gap-2">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            {step < BS_STEPS.length - 1 ? (
              <Button onClick={() => setStep(s => s + 1)} data-testid="button-bs-wizard-next" className="gap-2">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => exportBSPDF(data)} data-testid="button-export-bs-wizard" className="gap-2">
                  <Download className="w-4 h-4" /> {t("financial:wizard.exportPdf")}
                </Button>
                <Button
                  onClick={() => saveMutation.mutate(buildBSBody(data))}
                  disabled={
                    saveMutation.isPending
                    || !data.dealershipName
                    || (data.periodPrecision === "exact" && !data.asOfDate)
                    || (data.periodPrecision === "year_only" && (
                      !data.periodYear
                      || !Number.isInteger(Number(data.periodYear))
                      || Number(data.periodYear) < 1900
                      || Number(data.periodYear) > 9999
                    ))
                  }
                  data-testid="button-save-bs-statement"
                  className="gap-2"
                >
                  {saveMutation.isPending ? "Saving…" : editStatement ? "Update Statement" : "Save to Documents"}
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="w-60 shrink-0 hidden lg:block">
          <div className="sticky top-[120px]">
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" /> {t("financial:wizard.balanceSummary")}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {([
                  ["Total Assets", totals.totalAssets],
                  ["Total Liabilities", totals.totalLiabilities],
                  ["Owner's Equity", totals.ownersEquity],
                ] as [string, number][]).map(([label, value]) => (
                  <div key={label} className="flex justify-between py-1.5 border-b last:border-0 border-border/50">
                    <span className="text-xs font-medium">{label}</span>
                    <span className={`text-sm font-bold tabular-nums ${value >= 0 ? "" : "text-rose-600 dark:text-rose-400"}`}>
                      {value !== 0 ? fmt$(value) : "—"}
                    </span>
                  </div>
                ))}
                {totals.totalAssets > 0 && (
                  <div className={`text-xs text-center mt-2 p-2 rounded-lg ${totals.isBalanced ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-amber-500/10 text-amber-700 dark:text-amber-300"}`}>
                    {totals.isBalanced ? "✓ Sheet balanced" : "Sheet not yet balanced"}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab Panel: P&L ──────────────────────────────────────────────────────────

function PLTabPanel({
  statements,
  isLoading,
  isActive,
}: {
  statements: FinancialStatement[] | undefined;
  isLoading: boolean;
  isActive: boolean;
}) {
  const { t } = useTranslation(["financial", "common"]);
  type PLMode = "list" | "wizard" | "detail";
  const [mode, setMode] = useState<PLMode>("list");
  const [detailStatement, setDetailStatement] = useState<FinancialStatement | null>(null);
  const [editStatement, setEditStatement] = useState<FinancialStatement | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/financial-statements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/financial-statements"] });
      toast({ title: t("financial:toasts.statementDeleted"), description: t("financial:toasts.plDeletedDesc") });
    },
    onError: () => {
      toast({ title: t("financial:toasts.deleteFailed"), description: t("financial:toasts.deleteFailedDesc"), variant: "destructive" });
    },
  });

  const handleView = (stmt: FinancialStatement) => {
    setDetailStatement(stmt);
    setMode("detail");
  };
  const handleExport = (stmt: FinancialStatement) => exportPLPDF(buildPLDataFromStatement(stmt));
  const handleEdit = (stmt: FinancialStatement) => { setEditStatement(stmt); setMode("wizard"); };
  const handleDelete = (stmt: FinancialStatement) => deleteMutation.mutate(stmt.id);
  const handleBackToList = () => { setDetailStatement(null); setEditStatement(null); setMode("list"); };

  return (
    <div style={{ display: isActive ? undefined : "none" }}>
      {mode === "wizard" && (
        <PLWizard onClose={handleBackToList} editStatement={editStatement ?? undefined} />
      )}
      {mode === "detail" && detailStatement && (
        <PLDetailView stmt={detailStatement} onBack={handleBackToList} />
      )}
      {mode === "list" && (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold" data-testid="heading-financial-statements">{t("financial:list.plHeading")}</h1>
              <p className="text-muted-foreground text-sm mt-1">{t("financial:list.plSubtitle")}</p>
            </div>
            <Button onClick={() => { setEditStatement(null); setMode("wizard"); }} data-testid="button-new-statement" className="gap-2">
              <Plus className="w-4 h-4" /> {t("financial:list.createNew")}
            </Button>
          </div>
          {isLoading ? (
            <div className="space-y-3" data-testid="skeleton-statements">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
            </div>
          ) : !statements || statements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center gap-4" data-testid="empty-state-statements">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <FileSpreadsheet className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-semibold text-lg">{t("financial:list.plEmptyTitle")}</p>
                <p className="text-muted-foreground text-sm mt-1 max-w-xs">
                  {t("financial:list.plEmptyDesc")}
                </p>
              </div>
              <Button onClick={() => { setEditStatement(null); setMode("wizard"); }} data-testid="button-get-started" className="gap-2">
                <Plus className="w-4 h-4" /> {t("financial:list.createPl")}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {statements.map(stmt => (
                <StatementCard key={stmt.id} stmt={stmt} onView={handleView} onExport={handleExport} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Tab Panel: Balance Sheet ─────────────────────────────────────────────────

function BSTabPanel({
  statements,
  isLoading,
  isActive,
}: {
  statements: FinancialStatement[] | undefined;
  isLoading: boolean;
  isActive: boolean;
}) {
  const { t } = useTranslation(["financial", "common"]);
  type BSMode = "list" | "wizard" | "detail";
  const [mode, setMode] = useState<BSMode>("list");
  const [detailStatement, setDetailStatement] = useState<FinancialStatement | null>(null);
  const [editStatement, setEditStatement] = useState<FinancialStatement | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/financial-statements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/financial-statements"] });
      toast({ title: t("financial:toasts.statementDeleted"), description: t("financial:toasts.bsDeletedDesc") });
    },
    onError: () => {
      toast({ title: t("financial:toasts.deleteFailed"), description: t("financial:toasts.deleteFailedDesc"), variant: "destructive" });
    },
  });

  const handleView = (stmt: FinancialStatement) => {
    setDetailStatement(stmt);
    setMode("detail");
  };
  const handleExport = (stmt: FinancialStatement) => exportBSPDF(buildBSDataFromStatement(stmt));
  const handleEdit = (stmt: FinancialStatement) => { setEditStatement(stmt); setMode("wizard"); };
  const handleDelete = (stmt: FinancialStatement) => deleteMutation.mutate(stmt.id);
  const handleBackToList = () => { setDetailStatement(null); setEditStatement(null); setMode("list"); };

  return (
    <div style={{ display: isActive ? undefined : "none" }}>
      {mode === "wizard" && (
        <BSWizard onClose={handleBackToList} editStatement={editStatement ?? undefined} />
      )}
      {mode === "detail" && detailStatement && (
        <BSDetailView stmt={detailStatement} onBack={handleBackToList} />
      )}
      {mode === "list" && (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold" data-testid="heading-bs-statements">{t("financial:list.bsHeading")}</h1>
              <p className="text-muted-foreground text-sm mt-1">{t("financial:list.bsSubtitle")}</p>
            </div>
            <Button onClick={() => { setEditStatement(null); setMode("wizard"); }} data-testid="button-new-bs-statement" className="gap-2">
              <Plus className="w-4 h-4" /> {t("financial:list.createNew")}
            </Button>
          </div>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
            </div>
          ) : !statements || statements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center gap-4" data-testid="empty-state-bs">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <FileSpreadsheet className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-semibold text-lg">{t("financial:list.bsEmptyTitle")}</p>
                <p className="text-muted-foreground text-sm mt-1 max-w-xs">
                  {t("financial:list.bsEmptyDesc")}
                </p>
              </div>
              <Button onClick={() => { setEditStatement(null); setMode("wizard"); }} data-testid="button-get-started-bs" className="gap-2">
                <Plus className="w-4 h-4" /> {t("financial:list.createBs")}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {statements.map(stmt => (
                <StatementCard key={stmt.id} stmt={stmt} onView={handleView} onExport={handleExport} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function FinancialStatementsPage() {
  const { t } = useTranslation(["financial", "common"]);
  const { data: authUser } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const [activeTab, setActiveTab] = useState<"pl" | "bs">("pl");

  const { data: allStatements, isLoading } = useQuery<FinancialStatement[]>({
    queryKey: ["/api/financial-statements"],
  });

  const plStatements = allStatements?.filter(s => (s.statementType ?? "pl") === "pl");
  const bsStatements = allStatements?.filter(s => s.statementType === "balance_sheet");

  if (authUser?.memberPermissions && !authUser.memberPermissions.financialAccess) {
    return <AccessRestricted feature="Financial Statements" />;
  }

  return (
    <div>
      <div className="border-b bg-background sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-3 flex gap-2">
          <TabButton active={activeTab === "pl"} onClick={() => setActiveTab("pl")} testId="tab-pl">
            {t("financial:tabs.pl")}
          </TabButton>
          <TabButton active={activeTab === "bs"} onClick={() => setActiveTab("bs")} testId="tab-bs">
            {t("financial:tabs.bs")}
          </TabButton>
        </div>
      </div>
      <PLTabPanel
        statements={plStatements}
        isLoading={isLoading}
        isActive={activeTab === "pl"}
      />
      <BSTabPanel
        statements={bsStatements}
        isLoading={isLoading}
        isActive={activeTab === "bs"}
      />
    </div>
  );
}
